import type { EmailTemplateData } from '@workspace/api-backend'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { FormField } from '@workspace/ui/components/form-field'
import { Switch } from '@workspace/ui/components/switch'
import { TextInput } from '@workspace/ui/components/text-input'
import { Textarea } from '@workspace/ui/components/textarea'
import { Send } from 'lucide-react'
import { useState } from 'react'

interface EmailTemplateFormProps {
  template: EmailTemplateData
  onSave: (values: { subject: string; body: string; enabled: boolean }) => void
  onSendTest: (values: { subject: string; body: string }) => void
  isSaving: boolean
  isSendingTest: boolean
}

/** Replace `{{key}}` tokens with values for the live preview (mirrors the backend). */
function renderPreview(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) => vars[key] ?? match)
}

export function EmailTemplateForm({
  template,
  onSave,
  onSendTest,
  isSaving,
  isSendingTest,
}: EmailTemplateFormProps) {
  const [subject, setSubject] = useState(template.subject)
  const [body, setBody] = useState(template.body)
  const [enabled, setEnabled] = useState(template.enabled)

  const sampleVars = Object.fromEntries(template.variables.map((v) => [v.key, v.sample]))
  const previewSubject = renderPreview(subject, sampleVars)
  const previewBody = renderPreview(body, sampleVars)

  return (
    <div className="space-y-6">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="min-w-0 truncate text-lg font-bold">{template.label}</h1>
        <div className="flex shrink-0 items-center gap-2">
          <SecondaryButton
            type="button"
            icon={<Send />}
            onClick={() => onSendTest({ subject, body })}
            loading={isSendingTest}
          >
            Send test to me
          </SecondaryButton>
          <CtaButton
            type="button"
            onClick={() => onSave({ subject, body, enabled })}
            loading={isSaving}
          >
            Save
          </CtaButton>
        </div>
      </div>

      {/* Enabled toggle */}
      <div className="flex items-center justify-between rounded-lg border border-border bg-input/20 px-4 py-3">
        <div>
          <p className="font-medium text-foreground">Enabled</p>
          <p className="text-sm text-muted-foreground">
            When off, this email is never sent (the master automated-emails switch still applies).
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} />
      </div>

      {!template.editable && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
          This email's content is generated dynamically and can't be edited here. Use the toggle
          above to enable or disable it.
        </div>
      )}

      {template.editable && (
        <>
          {/* Subject */}
          <FormField label="Subject" required>
            <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} required />
          </FormField>

          {/* Body */}
          <FormField label="Body" hint="Plain text. Use the variables below for personalization.">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              className="font-mono text-sm"
            />
          </FormField>

          {/* Variables reference */}
          {template.variables.length > 0 && (
            <FormField label="Available variables">
              <div className="space-y-1.5">
                {template.variables.map((v) => (
                  <div key={v.key} className="flex items-baseline gap-2 text-sm">
                    <code className="rounded bg-input/40 px-1.5 py-0.5 font-mono text-primary">
                      {`{{${v.key}}}`}
                    </code>
                    <span className="text-muted-foreground">{v.description}</span>
                  </div>
                ))}
              </div>
            </FormField>
          )}

          {/* Live preview */}
          <FormField label="Preview" hint="Rendered with sample values">
            <div className="overflow-hidden rounded-lg border border-border bg-background">
              <div className="border-b border-border bg-input/20 px-4 py-2 text-sm">
                <span className="text-muted-foreground">From: </span>
                <span className="text-foreground">David from SeedForge</span>
              </div>
              <div className="border-b border-border px-4 py-2 text-sm">
                <span className="text-muted-foreground">Subject: </span>
                <span className="font-medium text-foreground">{previewSubject}</span>
              </div>
              <div className="whitespace-pre-wrap px-4 py-3 text-sm text-foreground">
                {previewBody}
              </div>
            </div>
          </FormField>
        </>
      )}
    </div>
  )
}
